import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Logo from '@/components/base/Logo';
import LanguageSwitcher from '@/components/base/LanguageSwitcher';
import Input from '@/components/base/Input';
import Button from '@/components/base/Button';
import Alert from '@/components/base/Alert';
import { authApi } from '@/lib/api';

interface AdminLoginProps {
  onSuccess: (token: string) => void;
}

export default function AdminLogin({ onSuccess }: AdminLoginProps) {
  const { t } = useTranslation();
  const bootstrapped = useRef(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    authApi.bootstrap();
  }, []);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loading || locked) return;
    if (!email.trim() || !password) {
      setError(t('admin.login.error'));
      return;
    }
    setLoading(true);
    setError('');
    const result = await authApi.adminLogin(email.trim(), password);
    setLoading(false);

    if (result.ok && typeof result.token === 'string') {
      onSuccess(result.token);
      return;
    }

    switch (result.code) {
      case 'LOCKED':
        setLocked(true);
        setError(
          t('admin.login.locked', {
            minutes: typeof result.retryAfterMinutes === 'number' ? result.retryAfterMinutes : 10,
          }),
        );
        break;
      case 'IP_BLOCKED':
        setError(t('admin.login.blocked'));
        break;
      case 'INVALID_CREDENTIALS':
      case 'CODE_REQUIRED':
        setError(t('admin.login.error'));
        break;
      default:
        setError(t('errors.server'));
    }
  };

  return (
    <div className="min-h-[100vh] w-full flex flex-col bg-background-100">
      <div className="w-full px-4 md:px-8 h-16 flex items-center justify-between">
        <Logo to="/" />
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
        </div>
      </div>

      <main className="flex-1 w-full flex items-center justify-center px-4 md:px-8 py-6">
        <div className="w-full max-w-4xl grid lg:grid-cols-[0.9fr_1fr] rounded-3xl border border-background-200 bg-background-50 overflow-hidden">
          <section className="relative hidden lg:flex flex-col justify-between p-9 bg-foreground-950">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-background-50/20 bg-background-50/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-background-100 font-label">
                <span className="flex items-center justify-center w-3.5 h-3.5">
                  <i className="ri-shield-keyhole-line text-[13px]" aria-hidden="true" />
                </span>
                {t('admin.login.eyebrow')}
              </span>
              <p className="mt-8 text-[28px] font-semibold leading-[1.25] text-background-50 font-heading">
                {t('admin.login.panelTitle')}
              </p>
              <p className="mt-3 max-w-[280px] text-[13px] leading-relaxed text-background-200/80">
                {t('admin.login.panelText')}
              </p>
            </div>

            <div className="space-y-3">
              {[
                { icon: 'ri-lock-2-line', label: t('admin.login.meta1') },
                { icon: 'ri-history-line', label: t('admin.login.meta2') },
                { icon: 'ri-shield-check-line', label: t('admin.login.meta3') },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2.5">
                  <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-background-50/10 text-accent-300 shrink-0">
                    <i className={`${item.icon} text-sm`} aria-hidden="true" />
                  </span>
                  <span className="text-[12.5px] text-background-200 font-label">{item.label}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="p-6 sm:p-9">
            <h1 className="text-[24px] font-semibold leading-tight text-foreground-950">
              {t('admin.login.title')}
            </h1>
            <p className="mt-2 text-[13px] leading-relaxed text-foreground-500">{t('admin.login.subtitle')}</p>

            <form onSubmit={submit} className="mt-6 space-y-4" noValidate>
              <Input
                label={t('admin.login.email')}
                name="adminEmail"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="izisender@gmail.com"
                icon="ri-mail-line"
                autoComplete="username"
                disabled={locked}
              />
              <Input
                label={t('admin.login.password')}
                name="adminPassword"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="••••••••••"
                icon="ri-lock-password-line"
                autoComplete="current-password"
                disabled={locked}
                trailing={
                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    aria-label={showPassword ? t('admin.login.hide') : t('admin.login.show')}
                    className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-400 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} aria-hidden="true" />
                  </button>
                }
              />

              {error ? <Alert tone="error">{error}</Alert> : null}

              <Button type="submit" size="lg" fullWidth loading={loading} disabled={locked}>
                {loading ? t('admin.login.submitting') : t('admin.login.submit')}
              </Button>
            </form>

            <div className="mt-5 rounded-xl border border-dashed border-background-300 bg-background-100 px-3.5 py-3">
              <p className="flex items-start gap-2 text-[11.5px] leading-relaxed text-foreground-500">
                <span className="flex items-center justify-center w-3.5 h-3.5 mt-0.5 shrink-0 text-primary-600">
                  <i className="ri-information-line text-[13px]" aria-hidden="true" />
                </span>
                <span>{t('admin.login.demo')}</span>
              </p>
            </div>
          </section>
        </div>
      </main>

      <footer className="w-full border-t border-background-200 bg-background-50">
        <div className="w-full px-4 md:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11.5px] text-foreground-500">
          <p className="font-label">{t('login.footer.rights', { year: new Date().getFullYear() })}</p>
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <span className="flex items-center justify-center w-3.5 h-3.5">
              <i className="ri-shield-check-line text-[13px]" aria-hidden="true" />
            </span>
            {t('login.footer.security')}
          </span>
        </div>
      </footer>
    </div>
  );
}